import { useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bot, ChevronDown, ChevronUp, PencilLine, Sparkles, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cleanMarkdownBold } from '@/lib/utils';
import type { AIModel, Message } from '@/types';
import TypingIndicator from './TypingIndicator';
import DeepResearchMessageCard from './DeepResearchMessageCard';

interface CollabMessageGroup {
  id: string;
  userMessage?: Message;
  drafterMessages: Message[];
  editorMessage?: Message;
  assistantMessages?: Message[];
}

interface CollabMessageCardProps {
  group: CollabMessageGroup;
  models: AIModel[];
}

const getModelName = (models: AIModel[], modelId?: string | null) => {
  if (!modelId) return 'Unknown model';
  return models.find((model) => model.id === modelId)?.name || 'Unavailable model';
};

const renderContent = (content?: string) => {
  if (!content) return null;
  return (
    <div className="prose prose-sm max-w-none dark:prose-invert">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanMarkdownBold(content)}</ReactMarkdown>
    </div>
  );
};

export default function CollabMessageCard({ group, models }: CollabMessageCardProps) {
  const [draftsExpanded, setDraftsExpanded] = useState(true);
  const assistantMessages = group.assistantMessages || [
    ...group.drafterMessages,
    ...(group.editorMessage ? [group.editorMessage] : []),
  ];
  const isDeepResearch = useMemo(
    () => assistantMessages.some(
      (message) => message.metadata?.research?.workflow === 'deep_research' || message.collab_role === 'orchestrator'
    ),
    [assistantMessages]
  );

  if (isDeepResearch) {
    return <DeepResearchMessageCard group={{ id: group.id, userMessage: group.userMessage, assistantMessages }} models={models} />;
  }

  const draftCount = group.drafterMessages.length;

  return (
    <div className="rounded-2xl border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge className="bg-sky-600 text-white hover:bg-sky-600">Collab</Badge>
          <span className="text-sm text-muted-foreground">{draftCount} draft{draftCount === 1 ? '' : 's'} → final editor pass</span>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => setDraftsExpanded((value) => !value)}>
          {draftsExpanded ? (
            <>
              Hide drafts
              <ChevronUp className="ml-1 h-4 w-4" />
            </>
          ) : (
            <>
              Show drafts
              <ChevronDown className="ml-1 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      <div className="mt-4 rounded-xl border bg-muted/20 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4" />
          User prompt
        </div>
        <div className="text-sm whitespace-pre-wrap text-foreground">{group.userMessage?.content || 'Prompt unavailable'}</div>
      </div>

      {draftsExpanded ? (
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <PencilLine className="h-4 w-4 text-sky-500" />
            Draft stage
          </div>
          <div className="grid gap-3 lg:grid-cols-2">
            {group.drafterMessages.map((draft) => {
              const isPending = draft.collab_status === 'pending' || draft.collab_status === 'running' || !draft.content;
              const isError = draft.collab_status === 'error';

              return (
                <div
                  key={draft.id}
                  className={`rounded-xl border p-4 ${
                    isError ? 'border-destructive/40 bg-destructive/5' : 'bg-background'
                  }`}
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <Badge variant="outline">{getModelName(models, draft.collab_model || draft.model)}</Badge>
                    {isPending ? <Badge variant="secondary">Drafting…</Badge> : isError ? <Badge variant="destructive">Failed</Badge> : <Badge variant="secondary">Done</Badge>}
                  </div>
                  {isPending ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>Waiting for drafter response</span>
                      <TypingIndicator />
                    </div>
                  ) : isError ? (
                    <p className="text-sm text-destructive">{draft.content}</p>
                  ) : (
                    renderContent(draft.content)
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      <Separator className="my-4" />

      <div className="rounded-2xl border border-violet-300 bg-violet-50/70 p-4 dark:border-violet-700 dark:bg-violet-950/20">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-violet-700 dark:text-violet-300">
            <Bot className="h-4 w-4" />
            Final editor
            <Sparkles className="h-4 w-4" />
          </div>
          <Badge className="bg-violet-600 text-white hover:bg-violet-600">{getModelName(models, group.editorMessage?.collab_model || group.editorMessage?.model)}</Badge>
        </div>
        {group.editorMessage?.collab_status === 'pending' || group.editorMessage?.collab_status === 'running' || !group.editorMessage?.content ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Waiting for drafts before generating the final answer</span>
            <TypingIndicator />
          </div>
        ) : group.editorMessage?.collab_status === 'error' ? (
          <p className="text-sm text-destructive">{group.editorMessage.content}</p>
        ) : (
          renderContent(group.editorMessage.content)
        )}
      </div>
    </div>
  );
}
